import React, { useMemo, useState } from 'react';
import { STAGES, stageOf } from '../lib/ticketStage';
import './ContractorKanban.css';

/**
 * The contractor's work as a board.
 *
 * Gordon: "create a kanban style board with the tickets for the contractor
 * view."
 *
 * THE COLUMNS ARE lib/ticketStage, NOT A LIST WRITTEN HERE. That file is a
 * partition - every ticket is in exactly one stage, and the counts add up to
 * the total - and it is shared with the tabs below, the admin tickets list and
 * the portal's own tiles. A board with its own idea of the columns would be a
 * fourth opinion about where a ticket is, and the one people would believe,
 * because it is the one that looks authoritative.
 *
 * ONLY THREE COLUMNS TAKE A DROP, and that is the server's rule rather than a
 * choice made here. routes/time-entries.js holds CONTRACTOR_STATUSES to
 * 'In Progress', 'On Hold' and 'Resolved' - a contractor cannot put a ticket
 * back to Open, cannot approve it and cannot close it. So the other columns
 * are not drop targets at all. A card that can be dragged anywhere and springs
 * back from half of it is worse than one that cannot be picked up: it teaches
 * people the board is broken.
 *
 * DRAGGING IS NEVER THE ONLY WAY. Every card carries a "Move to" control as
 * well, for three reasons that each rule out drag-only on their own: HTML5
 * drag-and-drop does not work with a keyboard, it does not work on a
 * touchscreen, and this portal is used on a phone on somebody's way to a job.
 * The menu is the real control; the dragging is a convenience on top of it.
 *
 * ONE RULE FOR MOVING A TICKET. `onMove` is ContractorDashboard's own
 * handleStatusUpdate - the same function the list view's dropdown calls, which
 * patches /tickets/:id/status and knows what to do when the server refuses.
 * Nothing about moving a ticket is reimplemented here.
 */

/** Which status a column means, for the ones a contractor may actually set. */
const DROP_STATUS = {
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  // Resolved lives in the unbilled stage: lib/ticketStage explains why there
  // is no Resolved column of its own - invoicing is what closes a ticket, so
  // anything resolved and uninvoiced is by definition unbilled.
  unbilled: 'Resolved'
};

const TONE = {
  Critical: 'crit',
  High: 'high',
  Medium: 'med',
  Low: 'low'
};

const ContractorKanban = ({ tickets = [], onMove, moving = null, onOpen }) => {
  // Which column a card is hovering over. Purely visual, but without it
  // nobody can tell where a drop will land.
  const [over, setOver] = useState(null);
  const [dragging, setDragging] = useState(null);

  const columns = useMemo(() => {
    const by = {};
    for (const s of STAGES) by[s.key] = [];
    for (const t of tickets) {
      const k = stageOf(t);
      (by[k] || (by[k] = [])).push(t);
    }
    return by;
  }, [tickets]);

  const move = (ticket, stageKey) => {
    const status = DROP_STATUS[stageKey];
    if (!status || !onMove) return;
    if (stageOf(ticket) === stageKey) return;   // already there
    onMove(ticket.id, status);
  };

  const onDrop = (e, stageKey) => {
    e.preventDefault();
    setOver(null);
    setDragging(null);
    const id = e.dataTransfer.getData('text/plain');
    const ticket = tickets.find((t) => String(t.id) === String(id));
    if (ticket) move(ticket, stageKey);
  };

  return (
    <div className="kb">
      <p className="kb-note">
        Drag a ticket, or use <strong>Move to</strong> on the card. You can move
        work into <strong>In progress</strong>, <strong>On hold</strong> and{' '}
        <strong>Unbilled</strong>; the other columns are set by the office.
      </p>

      <div className="kb-board">
        {STAGES.map((s) => {
          const list = columns[s.key] || [];
          const droppable = Boolean(DROP_STATUS[s.key]);
          return (
            <section
              key={s.key}
              className="kb-col"
              /*
               * The stage's own key, carried onto the element so the
               * stylesheet can give each column its colour. Gordon: "The board
               * needs a little bit of contrast to distinguish the different
               * stages as well as the different tickets."
               *
               * The key rather than an index, so inserting a stage into
               * lib/ticketStage does not silently repaint the five after it.
               */
              data-stage={s.key}
              data-droppable={droppable ? '' : undefined}
              data-over={over === s.key ? '' : undefined}
              aria-label={`${s.label}, ${list.length} ticket${list.length === 1 ? '' : 's'}`}
              onDragOver={(e) => {
                if (!droppable) return;
                // preventDefault is what makes a drop legal at all; without it
                // the browser refuses every drop and the board looks broken.
                e.preventDefault();
                setOver(s.key);
              }}
              onDragLeave={() => setOver((k) => (k === s.key ? null : k))}
              onDrop={(e) => droppable && onDrop(e, s.key)}
            >
              <header className="kb-col-head">
                <h3 className="kb-col-name">{s.label}</h3>
                <span className="kb-col-count">{list.length}</span>
              </header>
              {/* Everything below the heading strip, which runs edge to edge
                  so the stage's colour reaches the column's own border. */}
              <div className="kb-col-body">
              <p className="kb-col-hint">{s.hint}</p>

              {list.length === 0 ? (
                <p className="kb-empty">
                  {droppable && dragging ? 'Drop here' : 'Nothing here'}
                </p>
              ) : (
                <ol className="kb-cards">
                  {list.map((t) => (
                    <li
                      key={t.id}
                      className="kb-card"
                      data-moving={moving === t.id ? '' : undefined}
                      draggable={Boolean(onMove)}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', String(t.id));
                        e.dataTransfer.effectAllowed = 'move';
                        setDragging(t.id);
                      }}
                      onDragEnd={() => { setDragging(null); setOver(null); }}
                    >
                      <div className="kb-card-top">
                        <button type="button" className="kb-num"
                          onClick={() => onOpen && onOpen(t)}>
                          {t.ticket_number}
                        </button>
                        {t.priority && (
                          <span className="kb-pri" data-tone={TONE[t.priority] || 'low'}>
                            {t.priority}
                          </span>
                        )}
                      </div>
                      <p className="kb-title">{t.title}</p>

                      {/* An approver handed this one back and said why. It is
                          the one thing on a card that needs doing before the
                          work can continue, so it is not hidden behind a
                          click. */}
                      {t.sent_back_reason && (
                        <p className="kb-back">Sent back: {t.sent_back_reason}</p>
                      )}

                      {onMove && (
                        <label className="kb-move">
                          <span className="kb-move-label">Move to</span>
                          <select
                            value=""
                            disabled={moving === t.id}
                            onChange={(e) => {
                              const k = e.target.value;
                              e.target.value = '';
                              if (k) move(t, k);
                            }}
                          >
                            <option value="">
                              {moving === t.id ? 'Moving…' : 'Choose…'}
                            </option>
                            {Object.entries(DROP_STATUS)
                              .filter(([k]) => k !== stageOf(t))
                              .map(([k, status]) => (
                                <option key={k} value={k}>{status}</option>
                              ))}
                          </select>
                        </label>
                      )}
                    </li>
                  ))}
                </ol>
              )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default ContractorKanban;
